import { Link } from "react-router";
import { Icon } from "#app/components/ui/icon.tsx";
import {
	CATEGORY_LABELS,
	type BlogPostListItem,
	type Category,
} from "#app/utils/blog-types.ts";

interface BlogCardProps {
	post: BlogPostListItem;
}

export function BlogCard({ post }: BlogCardProps) {
	const { frontmatter, slug, readingTime } = post;
	const categoryLabel =
		CATEGORY_LABELS[frontmatter.category as Category] ??
		frontmatter.category;

	return (
		<article className="group rounded-lg border border-border bg-card p-6 transition-colors hover:border-blue-300 dark:hover:border-blue-700">
			<Link
				to={`/blog/${slug}`}
				className="no-underline hover:no-underline"
			>
				{/* Hero image thumbnail */}
				{frontmatter.heroImage ? (
					<img
						src={frontmatter.heroImage.src}
						alt={frontmatter.heroImage.alt}
						className="mb-4 h-48 w-full rounded-md border border-border object-cover"
						loading="lazy"
					/>
				) : null}

				{/* Category badge */}
				<span className="mb-2 inline-block rounded-full bg-blue-50 px-2.5 py-0.5 text-body-2xs font-medium text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
					{categoryLabel}
				</span>

				{/* Title */}
				<h3
					className="mb-2 text-h5 text-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400"
					style={{ fontFamily: "var(--serif-head)" }}
				>
					{frontmatter.title}
				</h3>

				{/* Description */}
				<p className="mb-4 text-body-sm text-muted-foreground line-clamp-2">
					{frontmatter.description}
				</p>

				{/* Metadata */}
				<div className="flex flex-wrap items-center gap-3 text-body-2xs text-muted-foreground">
					<span className="flex items-center gap-1">
						<Icon name="calendar" className="size-3" />
						<time dateTime={frontmatter.date}>
							{new Date(
								frontmatter.date + "T00:00:00",
							).toLocaleDateString("en-US", {
								year: "numeric",
								month: "short",
								day: "numeric",
							})}
						</time>
					</span>
					<span className="flex items-center gap-1">
						<Icon name="clock" className="size-3" />
						{readingTime.text}
					</span>
				</div>

				{/* Tags */}
				<div className="mt-3 flex flex-wrap gap-1.5">
					{frontmatter.tags.slice(0, 3).map((tag) => (
						<span
							key={tag}
							className="rounded bg-muted px-2 py-0.5 text-body-2xs text-muted-foreground"
						>
							{tag}
						</span>
					))}
				</div>
			</Link>
		</article>
	);
}
